import React, { useEffect, useRef } from "react";
import "codemirror/lib/codemirror.css";
import { UnControlled as CodeMirror } from "react-codemirror2";
import styled from "styled-components";
import { Radius, Transition, dominant, form, message } from "@glossy";
import { useOnClickOutside } from "@lkf/hooks";
import Icon from "@ui-kit/icon";
import { MODE } from "./linkfluence-query";
import { tokenHighlights } from "./token";

type PossibleEvent = MouseEvent | TouchEvent;

type ContainerProps = {
  error: boolean;
  clearable: boolean;
};

const Container = styled.div<ContainerProps>`
  position: relative;
  ${tokenHighlights};

  .CodeMirror {
    padding-right: ${(props) => props.clearable && "48px"};
    height: auto;
    overflow: hidden;
    border-radius: ${Radius.Small} !important;
    background-color: transparent !important;
    border: 1px solid
      ${(props) =>
        props.error
          ? message(props, "error")
          : form(props, "primary")} !important;
    transition: border-color ${Transition.Color} !important;
    color: ${form} !important;
  }

  .CodeMirror-cursor {
    border-left: solid 1px ${form};
  }

  .CodeMirror-scroll {
    height: auto;
    overflow-y: hidden !important;
    overflow-x: auto !important;
  }

  .CodeMirror:not(.CodeMirror-focused):hover {
    border-color: ${(props) =>
      props.error
        ? message(props, "error")
        : form(props, "primary")} !important;
  }

  .CodeMirror-focused {
    border-color: ${(props) =>
      props.error ? message(props, "error") : dominant(props)} !important;
  }
`;

const Placeholder = styled.div`
  position: absolute;
  line-height: 33px;
  font-size: 12px;
  color: ${form};
  font-style: italic;
  font-weight: bold;
  top: 0;
  bottom: 0;
  left: 12px;
  overflow: hidden;
  right: 5px;
  pointer-events: none;
`;

const Cross = styled(Icon)`
  position: absolute;
  top: 9px;
  right: 8px;
  color: ${form};
  cursor: pointer;
  &:hover {
    color: ${(props) => message(props, "error")};
  }
`;

const OPTIONS = {
  viewportMargin: Infinity,
  lineWrapping: true,
  scrollbarStyle: null,
  mode: "linkfluence-query",
};

interface IProps {
  onFocus?: () => void;
  value?: string;
  onChange?: (newValue: string) => void;
  onEnter?: (newValue: string) => void;
  options?: any;
  error?: boolean;
  placeholder?: string;
  clearable?: boolean;
  selection?: string;
  onClickOutside?: (event: PossibleEvent) => void;
}

const KomonFormCodemirror = ({
  onFocus,
  value,
  onChange,
  onEnter,
  options = {},
  error = false,
  placeholder,
  clearable = false,
  selection,
  onClickOutside,
}: IProps) => {
  const codeMirrorRef = useRef<any>(null);
  const refContainer = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const codemirror = codeMirrorRef.current?.editor;
    codemirror.setOption("extraKeys", {
      Enter: (cm) => {
        if (onEnter) {
          onEnter(cm.getValue());
          cm.getInputField().blur();
        }
        return cm.Pass;
      },
    });
  }, [onEnter]);
  useEffect(() => {
    if (selection) {
      const codemirror = codeMirrorRef.current?.editor;
      const value = codemirror.getValue();
      const currentSelection = codemirror.getSelection();
      const prefix =
        currentSelection.length > 0
          ? currentSelection.charAt(0) === " "
            ? ""
            : " "
          : value.charAt(value.length - 1) === " "
          ? ""
          : " ";

      codemirror.replaceSelection(prefix + selection);
      codemirror.focus();
    }
  }, [selection]);
  useEffect(() => {
    const codemirror = codeMirrorRef.current?.editor;
    const currentValue = codemirror.getValue();
    if (currentValue !== value) {
      codemirror.setValue(value);
    }
  }, [value]);
  useOnClickOutside(refContainer, (event) => {
    onClickOutside?.(event);
  });
  return (
    <Container error={error} clearable={clearable} ref={refContainer}>
      <CodeMirror
        ref={codeMirrorRef}
        defineMode={MODE}
        options={{ ...OPTIONS, ...options }}
        value={value}
        onFocus={() => {
          onFocus?.();
        }}
        onChange={(editor, data, value) => {
          onChange?.(value);
        }}
      />
      {!!placeholder && value === "" && (
        <Placeholder>{placeholder}</Placeholder>
      )}
      {clearable && value !== "" && (
        <Cross glyph="circle-cross" onClick={() => onChange?.("")} />
      )}
    </Container>
  );
};

export default KomonFormCodemirror;
